import type { FC, ReactNode } from 'react'
import type { SellerVerification } from '@/lib/marketplace/seller-verification'

import { cn } from '@/lib/utils'
import { SellerVerificationIcon } from '@/components/marketplace/seller/seller-verification-badges'

interface SellerVerificationPanelProps {
    verifications: Array<SellerVerification>
    /** The site-wide "MTA is not a party to the deal" line. */
    disclosure?: string
    className?: string
}

/**
 * The verification rail. Every entry states plainly whether MTA performed that
 * check, and an unperformed check is shown greyed and labelled "Not verified"
 * rather than hidden, because absence is exactly what a buyer needs to see.
 */
export const SellerVerificationPanel: FC<SellerVerificationPanelProps> = ({
    verifications,
    disclosure,
    className
}): ReactNode => {
    return (
        <div className={cn('mta-border rounded-mta-lg bg-paper p-6', className)}>
            <h2 className='type-display text-xl text-ink-900'>
                Verification
            </h2>

            <span aria-hidden='true' className='mt-2.5 block h-[3px] w-14 bg-brand-red' />

            <ul className='mt-6 space-y-6'>
                {verifications.map((item) => (
                    <li key={item.key} className='flex items-start gap-3'>
                        <SellerVerificationIcon verification={item} className='mt-0.5 size-[18px]' />

                        <div className='min-w-0'>
                            <p
                                className={cn(
                                    'text-sm font-bold leading-snug',
                                    item.granted ? 'text-ink-900' : 'text-text-muted'
                                )}
                            >
                                {item.granted ? item.label : `${item.shortLabel} not verified`}
                            </p>

                            <p className='mt-1.5 text-xs leading-relaxed text-text-muted'>
                                {item.granted
                                    ? item.description
                                    : 'MTA has not completed this check for this seller.'}
                            </p>
                        </div>
                    </li>
                ))}
            </ul>

            {disclosure && (
                <p className='mt-7 border-t border-ink-900/15 pt-5 text-[11px] leading-relaxed text-text-muted'>
                    {disclosure}
                </p>
            )}
        </div>
    )
}
